import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { 
  TokenUsage, 
  PerformanceLog, 
  KnowledgeEffectiveness,
  SummaryStats 
} from '@/components/ai-assistant/management/types';

interface AnalyticsOptions {
  startDate: Date;
  endDate: Date;
  topic?: string;
  limit?: number;
}

export function useAnalytics(options: AnalyticsOptions) {
  const { startDate, endDate, topic, limit = 100 } = options;
  const { user } = useAuth();
  
  const [isLoading, setIsLoading] = useState(true);
  const [tokenUsageData, setTokenUsageData] = useState<TokenUsage[]>([]);
  const [responseTimeData, setResponseTimeData] = useState<PerformanceLog[]>([]);
  const [knowledgeEffectivenessData, setKnowledgeEffectivenessData] = useState<KnowledgeEffectiveness[]>([]);
  const [summaryStats, setSummaryStats] = useState<SummaryStats>({
    totalTokens: 0,
    estimatedCost: 0,
    avgResponseTime: 0,
    cacheHitRate: 0,
    totalRequests: 0
  });

  useEffect(() => {
    const fetchAnalyticsData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([
          fetchTokenUsage(),
          fetchResponseTimes(),
          fetchKnowledgeEffectiveness(),
          fetchSummaryStats()
        ]);
      } catch (error) {
        console.error('Error fetching analytics data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchTokenUsage = async () => {
      let query = supabase
        .from('ai_token_usage')
        .select('*')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .order('created_at', { ascending: true })
        .limit(limit);

      const { data, error } = await query;
      
      if (error) {
        console.error('Error fetching token usage:', error);
        return;
      }
      
      if (data) {
        // Process data to group by date and calculate daily totals
        const processedData = processTokenUsageData(data);
        setTokenUsageData(processedData);
      }
    };

    const fetchResponseTimes = async () => {
      // Fix: Use simplified join to avoid complex nested relationships
      let query = supabase
        .from('ai_performance_logs')
        .select('*')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());
        
      if (topic) {
        // Instead of trying to filter by a joined field, we'll process this client-side
        // This avoids the complex join syntax that's causing errors
      }
      
      query = query.limit(limit);

      const { data, error } = await query;
      
      if (error) {
        console.error('Error fetching response times:', error);
        return;
      }
      
      if (data) {
        // Process data to group by date and operation type
        const processedData = processResponseTimeData(data);
        
        // If topic filter is specified, filter the processed data client-side
        const filteredData = topic 
          ? processedData.filter(item => item.topic === topic)
          : processedData;
          
        setResponseTimeData(filteredData);
      }
    };

    const fetchKnowledgeEffectiveness = async () => {
      // Create a query that properly includes the title from assistant_knowledge
      const { data, error } = await supabase
        .from('knowledge_effectiveness')
        .select(`
          *,
          assistant_knowledge:knowledge_document_id (title)
        `)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .order('relevance_score', { ascending: false })
        .limit(limit);
      
      if (error) {
        console.error('Error fetching knowledge effectiveness:', error);
        return;
      }
      
      if (data) {
        setKnowledgeEffectivenessData(data);
      }
    };

    const fetchSummaryStats = async () => {
      // Token usage stats
      const { data: tokenData, error: tokenError } = await supabase
        .from('ai_token_usage')
        .select('tokens_used, estimated_cost')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());
      
      if (tokenError) {
        console.error('Error fetching token summary:', tokenError);
        return;
      }
      
      // Response time stats
      const { data: perfData, error: perfError } = await supabase
        .from('ai_performance_logs')
        .select('total_duration, cached')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .eq('status', 'success');
      
      if (perfError) {
        console.error('Error fetching performance summary:', perfError);
        return;
      }
      
      if (tokenData && perfData) {
        const totalTokens = tokenData.reduce((sum, item) => sum + item.tokens_used, 0);
        const estimatedCost = tokenData.reduce((sum, item) => sum + (item.estimated_cost || 0), 0);
        const avgResponseTime = perfData.length > 0 
          ? perfData.reduce((sum, item) => sum + item.total_duration, 0) / perfData.length 
          : 0;
        const cachedRequests = perfData.filter(item => item.cached).length;
        const cacheHitRate = perfData.length > 0 
          ? (cachedRequests / perfData.length) * 100 
          : 0;
        
        setSummaryStats({
          totalTokens,
          estimatedCost,
          avgResponseTime,
          cacheHitRate,
          totalRequests: perfData.length
        });
      }
    };

    if (user) {
      fetchAnalyticsData();
    }
  }, [user, startDate, endDate, topic, limit]);

  // Helper function to process token usage data
  const processTokenUsageData = (data: any[]): TokenUsage[] => {
    // Group by date and sum tokens
    const groupedData = data.reduce<Record<string, TokenUsage>>((acc, item) => {
      const date = new Date(item.created_at).toISOString().split('T')[0];
      if (!acc[date]) {
        acc[date] = {
          date: date,
          completion: 0,
          embedding: 0,
          total: 0,
          cost: 0
        };
      }
      
      const requestType = item.request_type || 'other';
      if (requestType === 'completion') {
        acc[date].completion += item.tokens_used;
      } else if (requestType === 'embedding') {
        acc[date].embedding += item.tokens_used;
      }
      
      acc[date].total += item.tokens_used;
      acc[date].cost += (item.estimated_cost || 0);
      
      return acc;
    }, {});
    
    return Object.values(groupedData).sort((a, b) => 
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );
  };

  // Update the processResponseTimeData function to include topic information
  const processResponseTimeData = (data: any[]): PerformanceLog[] => {
    // Type for our grouped data
    type GroupedItem = {
      date: string;
      operation_type: string;
      avg_processing_time: number;
      avg_total_time: number;
      count: number;
      cached_count: number;
      cache_hit_rate?: number;
      topic?: string; // Make sure topic is included here
    };

    // Group by date and operation type
    const groupedData = data.reduce<Record<string, GroupedItem>>((acc, item) => {
      const date = new Date(item.created_at).toISOString().split('T')[0];
      const key = `${date}-${item.operation_type}`;
      
      if (!acc[key]) {
        acc[key] = {
          date: date,
          operation_type: item.operation_type,
          avg_processing_time: 0,
          avg_total_time: 0,
          count: 0,
          cached_count: 0,
          topic: item.topic // Include the topic from the data
        };
      }
      
      acc[key].avg_processing_time += item.processing_duration;
      acc[key].avg_total_time += item.total_duration;
      acc[key].count += 1;
      if (item.cached) {
        acc[key].cached_count += 1;
      }
      
      return acc;
    }, {});
    
    // Calculate averages
    const result: PerformanceLog[] = Object.values(groupedData).map(item => ({
      date: item.date,
      operation_type: item.operation_type,
      avg_processing_time: item.avg_processing_time / item.count,
      avg_total_time: item.avg_total_time / item.count,
      count: item.count,
      cached_count: item.cached_count,
      cache_hit_rate: (item.cached_count / item.count) * 100,
      topic: item.topic // Make sure to include the topic in the result
    }));
    
    return result.sort((a, b) => 
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );
  };

  return {
    isLoading,
    tokenUsageData,
    responseTimeData,
    knowledgeEffectivenessData,
    summaryStats
  };
}
